import { useState, useEffect, useCallback } from 'react'
import api from '../lib/axios'

/**
 * Fetches and caches available branches for a repository.
 * Respects server-side cache (1 hour) and offers manual refresh.
 */
export function useBranches(repositoryId) {
  const [branches, setBranches]   = useState([])
  const [loading, setLoading]     = useState(false)
  const [error, setError]         = useState(null)
  const [fromCache, setFromCache] = useState(false)
  const [cachedAt, setCachedAt]   = useState(null)

  const fetch = useCallback(async (force = false) => {
    if (!repositoryId) return
    setLoading(true)
    setError(null)
    try {
      const params = force ? { force: 1 } : {}
      const { data } = await api.get(`/repositories/${repositoryId}/branches`, { params })
      setBranches(data.branches ?? [])
      setFromCache(data.from_cache ?? false)
      setCachedAt(data.cached_at ?? null)
    } catch (err) {
      setError(err.response?.data?.message ?? 'Failed to load branches.')
    } finally {
      setLoading(false)
    }
  }, [repositoryId])

  useEffect(() => { fetch() }, [fetch])

  return { branches, loading, error, fromCache, cachedAt, refresh: () => fetch(true) }
}

/**
 * Loads and updates the full automation settings for a repository.
 */
export function useAutomation(repositoryId) {
  const [settings, setSettings] = useState(null)
  const [loading, setLoading]   = useState(true)
  const [saving, setSaving]     = useState(false)
  const [error, setError]       = useState(null)

  const load = useCallback(async () => {
    if (!repositoryId) return
    setLoading(true)
    try {
      const { data } = await api.get(`/repositories/${repositoryId}/automation`)
      setSettings(data)
    } catch (err) {
      setError(err.response?.data?.message ?? 'Failed to load automation settings.')
    } finally {
      setLoading(false)
    }
  }, [repositoryId])

  useEffect(() => { load() }, [load])

  const save = useCallback(async (updates) => {
    setSaving(true)
    setError(null)
    try {
      const { data } = await api.put(`/repositories/${repositoryId}/automation`, updates)
      setSettings(data)
      return { ok: true }
    } catch (err) {
      const msg = err.response?.data?.message ?? 'Failed to save.'
      setError(msg)
      return { ok: false, error: msg, errors: err.response?.data?.errors }
    } finally {
      setSaving(false)
    }
  }, [repositoryId])

  return { settings, loading, saving, error, save, reload: load }
}
